import { Progress, Tag, Tooltip } from "antd"
import moment from "moment"
import { useEffect, useRef, useState } from "react";
import { DownOutlined, FolderOpenFilled, GlobalOutlined, ShopFilled, UpOutlined } from "@ant-design/icons";
import { ButtonLZ } from "../../../../components/design/Buttons/ButtonLZ";

const KW_SCOPE: any = {
	DOMAIN: { icon: <GlobalOutlined />, name: "dominio" },
	GROUP: { icon: <FolderOpenFilled />, name: "grupo" },
	LOCAL: { icon: <ShopFilled />, name: "local" },
};

export const KeywordResultKeyword = (props: { keyword: string, scope?: string, inverted?: boolean, large?: boolean, hideScope?: boolean }) => {
	const scope = props.scope && !props.hideScope ? KW_SCOPE[props.scope] : null;
	const inverted = !!props.inverted;
	const large = !!props.large;
	const [expanded, setExpanded] = useState(false);
	const [isTruncated, setIsTruncated] = useState(false);
	const textRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		setExpanded(false);
	}, [props.keyword]);

	useEffect(() => {
		const el = textRef.current;
		if (!el || expanded) {
			setIsTruncated(false);
			return;
		}
		setIsTruncated(el.scrollWidth > el.clientWidth + 1);
	}, [props.keyword, expanded, large, scope]);

	const baseStyle = inverted
		? {
				display: 'inline-flex' as const,
				alignItems: expanded ? 'flex-start' as const : 'center' as const,
				justifyContent: 'space-between' as const,
				gap: 8,
				width: 'fit-content' as const,
				maxWidth: expanded ? 480 : 220,
				minWidth: 0,
				padding: '4px 10px',
				border: '1px solid #0f172a',
				borderRadius: 4,
				background: '#0f172a',
				fontSize: 12,
				color: '#e2e8f0',
				boxSizing: 'border-box' as const,
			}
		: {
				display: large ? 'inline-flex' as const : 'flex' as const,
				alignItems: expanded ? 'flex-start' as const : 'center' as const,
				justifyContent: 'space-between' as const,
				gap: large ? 10 : 8,
				width: large ? 'fit-content' as const : '100%',
				maxWidth: expanded ? (large ? 560 : 360) : (large ? 360 : 220),
				minWidth: 0,
				minHeight: large ? undefined : '100%',
				height: large && !expanded ? undefined : (large ? undefined : '100%'),
				padding: large ? '6px 14px' : '4px 10px',
				border: '1px solid #5eead4',
				borderLeft: large ? '5px solid #0f766e' : '5px solid #0f766e',
				borderRight: large ? '5px solid #0f766e' : '5px solid #0f766e',
				borderRadius: 6,
				background: '#ffffff',
				fontSize: large ? 15 : 12,
				color: '#64748b',
				boxShadow: large ? '0 1px 3px rgba(15, 23, 42, 0.08)' : undefined,
				boxSizing: 'border-box' as const,
			};

	return (
		<div style={baseStyle}>
			<div style={{
				display: 'inline-flex',
				alignItems: expanded ? 'flex-start' : 'center',
				gap: 5,
				minWidth: 0,
				flex: 1,
			}}>
				{!large && (scope?.icon || <ShopFilled style={{ flexShrink: 0, marginTop: expanded ? 2 : 0 }} />)}
				<Tooltip title={!expanded && isTruncated ? props.keyword : undefined}>
					<span
						ref={textRef}
						style={{
							fontWeight: large ? 700 : 600,
							color: inverted ? '#ffffff' : '#0f172a',
							whiteSpace: expanded ? 'normal' : 'nowrap',
							overflow: expanded ? 'visible' : 'hidden',
							textOverflow: expanded ? 'clip' : 'ellipsis',
							minWidth: 0,
							lineHeight: 1.25,
							wordBreak: expanded ? 'break-word' : undefined,
						}}
					>
						{props.keyword}
					</span>
				</Tooltip>
				{(isTruncated || expanded) && (
					<button
						type="button"
						aria-label={expanded ? 'Contraer keyword' : 'Expandir keyword'}
						onClick={(e) => {
							e.stopPropagation();
							setExpanded((prev) => !prev);
						}}
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							margin: expanded ? '2px 0 0' : 0,
							border: 'none',
							background: 'transparent',
							cursor: 'pointer',
							color: inverted ? '#e2e8f0' : '#0f766e',
							fontSize: large ? 12 : 11,
							lineHeight: 1,
							flexShrink: 0,
						}}
					>
						{expanded ? <UpOutlined /> : <DownOutlined />}
					</button>
				)}
			</div>
			{scope && (
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						flexShrink: 0,
						marginLeft: 'auto',
						fontSize: inverted || large ? '0.9em' : '0.85em',
						fontWeight: inverted || large ? 500 : 600,
						letterSpacing: '0.02em',
						color: inverted ? '#e2e8f0' : '#0f766e',
						background: inverted ? 'rgba(255,255,255,0.12)' : '#f0fdfa',
						border: inverted
							? '1px solid rgba(255,255,255,0.2)'
							: '1px solid #99f6e4',
						padding: inverted ? '1px 5px' : '1px 6px',
						borderRadius: inverted ? 3 : 999,
						whiteSpace: 'nowrap',
					}}
				>
					{scope.name}
				</span>
			)}
		</div>
	);
};

export const KeywordResultDistance = (props: {metric: any, muted?: boolean, compact?: boolean}) => {

	const metric  = props.metric
	const config = getConfig(metric.distanceDiff);
	const mutedTagStyle = props.muted ? {
		background: 'white',
		border: `1px solid color-mix(in srgb, ${config.color} 40%, white)`,
		color: config.color,
	} : {};
	const tagMargin = props.compact ? 0 : '0px 20px';

	return (
		metric.created ?
			<Tooltip title={				
				<div>	
					<div style={{marginTop:'15px'}}><b>{config.label}</b></div>				
					<div style={{marginTop:'8px', color:'lightgrey'}}><small>{config.description}</small></div>
				</div>}	>			
				{metric.popularityPosAVG > 0 ?
				<Tag
					style={{
						display: 'flex',
						justifyContent: 'center',
						height: '20px',
						width: '130px',
						margin: tagMargin,
						borderRadius: '10px',
						...mutedTagStyle,
					}}
					color={props.muted ? undefined : config.color}
				>
					{config.label}
				</Tag>
				:
				metric.withoutCompetitors ?
				 <Tag style={{display:'flex', justifyContent:'center', height:'20px', width: '130px', margin: tagMargin, borderRadius:'10px', border:'1px solid black', color:'black'}} color={'white'}>
					<b>Sin Competidores</b>
				</Tag>
				: 
				<Tag
					style={{
						display: 'flex',
						justifyContent: 'center',
						height: '20px',
						width: '130px',
						margin: tagMargin,
						borderRadius: '10px',
						...(props.muted ? { background: 'white', border: '1px solid color-mix(in srgb, #94a3b8 40%, white)', color: '#94a3b8' } : {}),
					}}
					color={props.muted ? undefined : 'black'}
				>
					No Relevante
				</Tag>
				}
			</Tooltip>
			:
			<NotApply/>
	)
}

export const KeywordResultDistanceSummary = (props: {metric: any}) => {

	const metric  = props.metric
	const config = getConfig(metric.distanceDiff);

	return (
		
		<>
			{metric.popularityPosAVG > 0 ?
			<Tag style={{display:'flex', justifyContent:'center', border:'solid 1px white', height:'20px', width: '160px', margin:'0px 20px', borderRadius:'10px'}} color={config.color}>
				{config.label}
			</Tag>
			:
			<Tag style={{display:'flex', justifyContent:'center', border:'solid 1px white', height:'20px', width: '160px', margin:'0px 20px', borderRadius:'10px'}} color={'black'}>
				No Relevante
			</Tag>
			}			
			<div style={{marginTop:'8px', color:'lightgrey'}}><small>{config.description}</small></div>
		</>	
	)
}

export const KeywordResultLocalpack = (props: {metric: any, strokeColor?: string}) => {

	const metric  = props.metric	
	const isPresent = !!metric.created
	const strokeColor = props.strokeColor || 'LimeGreen'
	const percent = Math.round(100 * (metric.localPackPercent ?? 0))
	const percentColor = percent < 5 ? '#b91c1c' : percent <= 15 ? '#d97706' : 'teal'
	
	return (		
		isPresent ? 
			<Tooltip title={				
				<div>	
					<div style={{marginTop:'15px'}}><b>El negocio tiene presencia en el localpack en un {percent}% de los puntos del grid</b></div>				
					<div style={{marginTop:'8px', color:'lightgrey'}}><small>El localpack representa las 3 primeras posiciones del ranking, estás tienen un ctr muy superior al resto de posiciones que vienen por detrás</small></div>
				</div>}	>			
				<Progress
					style={{width: '200px'}}
					size="small"
					strokeColor={strokeColor}
					trailColor="lavender"
					percent={percent}
					format={(p) => (
						<span style={{ color: percentColor, fontWeight: 700 }}>
							{p}%
						</span>
					)}
				/>
			</Tooltip>
			:
			<NotApply/>
	)
}

export const KeywordResultLocalpackReduced = (props: {metric: any}) => {

	const metric  = props.metric	
	const isPresent = !!metric.created
	const percent = Math.round(100 * (metric.localPackPercent ?? 0))
	const percentColor = percent < 5 ? '#b91c1c' : percent <= 15 ? '#d97706' : 'teal'
	
	return (		
		isPresent ? 
			<Tooltip title={				
				<div>	
					<div style={{marginTop:'15px'}}><b>El negocio tiene presencia en el localpack en un {percent}% de los puntos del grid</b></div>				
					<div style={{marginTop:'8px', color:'lightgrey'}}><small>El localpack representa las 3 primeras posiciones del ranking, estás tienen un ctr muy superior al resto de posiciones que vienen por detrás</small></div>
				</div>}	>			
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0, minWidth: 160, lineHeight: 1 }}>
					<span style={{ color: '#94a3b8', fontSize: 10, fontWeight: 500, lineHeight: 1, marginBottom: -2 }}>
						Presencia en Localpack
					</span>
					<Progress
						percent={percent}
						showInfo
						size="small"
						strokeColor="LimeGreen"
						trailColor="#e2e8f0"
						strokeWidth={6}
						format={(p) => (
							<span style={{ color: percentColor, fontWeight: 700, fontSize: 11 }}>
								{p}%
							</span>
						)}
						style={{ margin: 0, lineHeight: 1 }}
					/>
				</div>
			</Tooltip>
			:
			<NotApply/>
	)
}

export const KeywordResultDistanceLocalpack = (props: {metric: any, onClick: any}) => {

	const metric  = props.metric	

	return (
		metric.created ? 		
		<Tooltip title={				
			<div>	
				<div style={{marginTop:'15px'}}><b>Alcance de visibilidad de {metric.localPackMaxDistance.toFixed(0)} metros</b></div>				
				<div style={{marginTop:'8px', color:'lightgrey'}}><small>Si te alejas del negocio más de esa distancia dejas de aparecer en el localpack</small></div>
			</div>}	>			
			<big style={{display:'flex', justifyContent:'center', color: 'darkslategrey'}}>
				<ButtonLZ size='small' onClick={() => {props.onClick()}} type='link'>
					{metric.localPackMaxDistance.toFixed(0)} mts.
				</ButtonLZ>
			</big>
		</Tooltip>
		:
		<NotApply/>
	)
}

export const KeywordResultRankings = (props: {metric: any, onClick: any}) => {

	const metric  = props.metric	

	return (
		metric.created ? 				
			<big style={{display:'flex', justifyContent:'center', color: 'darkslategrey'}}>
				<ButtonLZ size='small' onClick={() => {props.onClick()}} type='link'>
					Ver Rankings
				</ButtonLZ>
			</big>		
		:
		<NotApply/>
	)
}

export const KeywordResultCreated = (props: {metric: any}) => {

	const metric  = props.metric	

	return (
		metric.created ? 		
		<small style={{display:'flex', justifyContent:'center'}}>
			{ isMoreThanAMonthAgo(metric.created) ? 
			<div style={{color: 'mediumvioletred'}}>{moment.utc(metric.created).fromNow()}</div>
			:
			<div style={{color: 'slategrey'}}>{moment.utc(metric.created).fromNow()}</div>
			}
		</small>
		:
		<NotApply/>
	)
}

const NotApply = () => {

return (
	<Tooltip title={				
		<div>	
			<div style={{marginTop:'15px'}}><b>No hay ningún escaneo en este periodo</b></div>
			<div style={{marginTop:'8px', color:'lightgrey'}}><small>Lanza un escaneo para poder obtener información relativa a esta keyword</small></div>
		</div>}	>
		<small style={{color:'silver', display:'flex', justifyContent:'center'}}><i>NA</i></small>				
	</Tooltip>
	)
}

export const getConfig = (positionDiff: number) => {
		
	if (positionDiff <= -5) {
		return {label: "Muy mejorable", color: "#a34d4d", description: "Google te ha penalizado por detrás de muchos locales que estaban más cerca"}
	} else if (positionDiff <= -1) {
		return {label: "Mejorable", color: "#c45c5c", description: "Google te ha penalizado por detrás de algunos locales que estaban más cerca"}
	} else if (positionDiff <= 1) {
		return {label: "Normal", color: "SlateGray", description: "Google te ha mantenido en una posición coherente a la distancia del local"}
	} else if (positionDiff <= 5) {
		return {label: "Buena", color: "SeaGreen", description: "Google te ha posicionado por delante de algunos locales que estaban más cerca"}
	} else {
		return {label: "Muy Buena", color: "LightSeaGreen", description: "Google te ha posicionado por delante de muchos locales que estaban más cerca"}
	}
};

function isMoreThanAMonthAgo(dateToCheck: any) {
        
	const now = moment();
	const oneMonthAgo = now.subtract(1, 'months');

	return moment(dateToCheck).isBefore(oneMonthAgo, 'months');
}